/** Additive protocol-v2 contracts. Nothing in this module changes v1 routing. */
import type { GameId, MatchId, SlotIndex } from '@djgg/core';
import type { GameplaySemantic, JsonValue } from '@djgg/gameplay-kit';
import type { MatchPhase, PlayerRef } from './envelope.js';
import type { MatchStateV1 } from './match.js';
import type { EndReasonV2, MatchResultV2 } from './resultV2.js';

export const PROTOCOL_V2 = 2 as const;
export type SupportedProtocolVersion = 1 | typeof PROTOCOL_V2;

/** A reconnect may become client-visible just before the room host finishes
 * reinstalling its protocol binding. Client and server share this bound so
 * retries cannot drift into a server-side malformed-message rejection. */
export const PROTOCOL_V2_RESUME_REQUEST_MAX_ATTEMPTS = 16;

/** Separate channels prevent an old handler from interpreting v2 payloads.
 * These names do not install handlers or change the version selected by a room. */
export const PROTOCOL_V2_MESSAGE_NAMES = Object.freeze({
  clientHello: 'v2:clientHello',
  seatNegotiation: 'v2:seatNegotiation',
  serverHello: 'v2:serverHello',
  protocolReject: 'v2:protocolReject',
  command: 'v2:command',
  commandResult: 'v2:commandResult',
  snapshot: 'v2:snapshot',
  eventCursorAck: 'v2:eventCursorAck',
  resumeRequest: 'v2:resumeRequest',
  resumeResult: 'v2:resumeResult',
  matchResult: 'v2:matchResult',
} as const);

/** UTF-8 byte limits are measured over canonical JSON. Visible-event bytes
 * include the array's brackets and commas, separately from the payload bound. */
export const PROTOCOL_V2_LIMITS = Object.freeze({
  commandBytes: 32 * 1024,
  /** Entire canonical command envelope, including rejected actions. Transport
   * adapters enforce this ceiling on raw frames before JSON decoding. */
  commandEnvelopeBytes: 64 * 1024,
  eventBytes: 8 * 1024,
  snapshotVisibleEvents: 256,
  snapshotVisibleEventBytes: 512 * 1024,
  snapshotPayloadBytes: 1024 * 1024,
  /** Includes all canonical JSON metadata/result/payload/events. Transport
   * decoders must also apply this bound before parsing an incoming frame. */
  snapshotBytes: 2 * 1024 * 1024,
  identityCharacters: 128,
  resumeTokenCharacters: 512,
  resumeTransportCharacters: 32,
  protocolRejectBytes: 2048,
  retryAfterMs: 60_000,
  reconnectAttempts: 32,
} as const);

export const PROTOCOL_V2_FEATURES = [
  'command-results',
  'event-cursor',
  'input-epoch',
  'match-config',
  'match-state',
  'replay-metadata',
  'semantic-events',
  'terminal-results',
  'viewer-projection',
] as const;
export type ProtocolFeatureV2 = (typeof PROTOCOL_V2_FEATURES)[number];

export const COMMAND_REJECT_REASONS = [
  'invalid-command',
  'match-not-active',
  'not-seated',
  'rate-limited',
  'simulation-rejected',
  'size-limit',
  'stale-epoch',
  'stale-sequence',
  'unauthorized',
  'server-error',
] as const;
export type CommandRejectReason = (typeof COMMAND_REJECT_REASONS)[number];

export type CommandDisposition = 'applied' | 'rejected' | 'queued';

/** Immediate local/online submission receipt. `queued` carries no authority. */
export interface CommandReceipt {
  epoch: number;
  seq: number;
  disposition: CommandDisposition;
  reason?: CommandRejectReason;
}

/** Game and lifecycle actions share one per-player epoch/sequence namespace.
 * A lifecycle action may not carry an unvalidated game payload. */
export type ClientActionV2<TCommand> =
  | { kind: 'game'; command: TCommand }
  | { kind: 'begin' }
  | { kind: 'pause' }
  | { kind: 'resume' }
  | { kind: 'concede' };

export interface CommandEnvelopeV2<TCommand> {
  v: 2;
  type: 'command';
  matchId: MatchId;
  playerId: ProtocolV2PlayerId;
  epoch: number;
  seq: number;
  action: ClientActionV2<TCommand>;
}

/** Asynchronous authoritative result for an online command. */
export interface CommandResultV2 {
  v: 2;
  type: 'commandResult';
  matchId: MatchId;
  playerId: ProtocolV2PlayerId;
  epoch: number;
  seq: number;
  appliedTick: number | null;
  status: 'applied' | 'rejected';
  reason?: CommandRejectReason;
}

export type EventAudienceV2 =
  | { kind: 'public' }
  | { kind: 'actor' }
  | { kind: 'target' }
  | { kind: 'slots'; slots: number[] }
  | { kind: 'teams'; teams: number[] }
  | { kind: 'spectators' };

/** Explicit semantic output of a game encoder. Only the host adds eventSeq;
 * a legacy event name or data shape never implies semantic meaning. */
export interface UnsequencedWorldEventV2 {
  tick: number;
  kind: string;
  semantic: GameplaySemantic;
  actorEntityId?: string;
  targetEntityId?: string;
  actorSlot?: number;
  targetSlot?: number;
  position?: { x: number; y: number };
  audience: EventAudienceV2;
  data?: JsonValue;
}

export interface WorldEventV2 extends UnsequencedWorldEventV2 {
  /** Host-owned match-scoped sequence, starting at 1. */
  eventSeq: number;
}

export const RESYNC_REASONS = [
  'command-result-window-overflow',
  'event-window-overflow',
  'revision-gap',
  'server-restart',
] as const;
export type ResyncReasonV2 = (typeof RESYNC_REASONS)[number];

export type ResyncMarkerV2 =
  | { kind: 'resume' }
  | { kind: 'full-resync'; reason: ResyncReasonV2 };

/** Full v2 frame. Authority arrays remain canonical for every recipient. */
export interface SnapshotEnvelopeV2<TPayload> {
  v: 2;
  gameId: GameId;
  matchId: MatchId;
  revision: number;
  tick: number;
  phase: MatchPhase;
  players: PlayerRef[];
  scores: number[];
  winnerSlot: SlotIndex | null;
  endReason: EndReasonV2;
  /** Canonical terminal result for terminal-results clients. Omission is only
   * for reading the pre-activation v2 draft; new writers send null until ended. */
  result?: MatchResultV2 | null;
  pausedBySlot: SlotIndex | null;
  countdownMs: number | null;
  events: WorldEventV2[];
  /** Highest global event sequence represented for this recipient. */
  eventCursor: number;
  /** Digest of the exact frozen MatchConfigV1 bytes. */
  configDigest: string;
  /** Highest authoritative command result represented for this recipient. */
  lastCommandResultSeq: number;
  resync: ResyncMarkerV2 | null;
  match?: MatchStateV1;
  payload: TPayload;
}

export interface ClientHelloV2 {
  v: 2;
  type: 'clientHello';
  supportedProtocols: SupportedProtocolVersion[];
  supportedFeatures: ProtocolFeatureV2[];
}

export interface EventRetentionV2 {
  maxEvents: number;
  maxEventBytes: number;
  maxEventAgeMs: number;
  maxCommandResultsPerPlayer: number;
  /** Exact canonical UTF-8 request plus authoritative result bytes, including
   * their complete metadata. Unrepresentable input retains an identity-only
   * rejection tombstone whose request bytes are counted instead. */
  maxCommandResultBytesPerPlayer: number;
  maxCommandResultAgeMs: number;
  /** Null iff no events remain, including after age eviction. A nonempty ring
   * starts at a positive sequence and ends at newestEventSeq. */
  oldestRetainedEventSeq: number | null;
  /** Persistent global high-water mark; only a match with no emitted events
   * has zero. Emptying the ring never resets it. */
  newestEventSeq: number;
}

/** Source-compatible exported name; the pre-activation v2 draft was not routed. */
export type EventRetentionBoundsV2 = EventRetentionV2;

export const DEFAULT_EVENT_RETENTION_LIMITS_V2 = Object.freeze({
  maxEvents: 2048,
  maxEventBytes: 4 * 1024 * 1024,
  maxEventAgeMs: 35_000,
  maxCommandResultsPerPlayer: 2048,
  maxCommandResultBytesPerPlayer: 2 * 1024 * 1024,
  maxCommandResultAgeMs: 35_000,
} as const);

export type ServerHelloV2 = {
  v: 2;
  type: 'serverHello';
  selectedProtocol: 2;
  features: ProtocolFeatureV2[];
  matchId: MatchId;
  playerId: ProtocolV2PlayerId;
  inputEpoch: number;
  runtimeContractDigest: string;
  /** Exact frozen MatchConfigV1 digest, checked before accepting any snapshot. */
  configDigest: string;
  retention: EventRetentionV2;
} & (
  /** A successful hello has a resolved role. Null means spectator, never a
   * missing player assignment or permission to use seat zero. */
  | { role: 'player'; seat: SlotIndex }
  | { role: 'spectator'; seat: null }
);

/** A bounded pre-hello outcome while authoritative seating is unresolved. */
export interface SeatNegotiationV2 {
  v: 2;
  type: 'seatNegotiation';
  status: 'waiting';
  matchId: MatchId;
  playerId: ProtocolV2PlayerId;
}

export const PROTOCOL_REJECT_REASONS_V2 = [
  'unsupported-protocol',
  'missing-feature',
  'runtime-mismatch',
  'seat-unavailable',
  'authentication-required',
  'match-expired',
  'server-capacity',
  'malformed-message',
] as const;
export type ProtocolRejectReasonV2 = (typeof PROTOCOL_REJECT_REASONS_V2)[number];

/** Direct response, including before any player identity exists. No arbitrary
 * error text, rejected payload, credential or stack may be attached. */
export type ProtocolRejectV2 = { v: 2; type: 'protocolReject' } & (
  | { reason: 'unsupported-protocol'; requiredProtocol: SupportedProtocolVersion }
  | { reason: 'missing-feature'; missingFeatures: ProtocolFeatureV2[] }
  | { reason: 'server-capacity'; retryAfterMs?: number }
  | { reason: Exclude<ProtocolRejectReasonV2, 'unsupported-protocol' | 'missing-feature' | 'server-capacity'> }
);

export type SeatNegotiationOutcomeV2 = SeatNegotiationV2 | ServerHelloV2 | ProtocolRejectV2;

export interface EventCursorAckV2 {
  v: 2;
  type: 'eventCursorAck';
  matchId: MatchId;
  eventCursor: number;
}

/** Identity/cursor data only. Resume credentials travel separately through
 * ResumeTransportPortV2 and never appear in a replay or diagnostic message. */
export interface ResumeRequestV2 {
  v: 2;
  type: 'resumeRequest';
  matchId: MatchId;
  playerId: ProtocolV2PlayerId;
  lastRevision: number;
  eventCursor: number;
  inputEpoch: number;
  lastCommandResultSeq: number;
}

export const RESUME_REJECT_REASONS = [
  'identity-mismatch',
  'match-not-found',
  'resume-expired',
  'unsupported-protocol',
] as const;
export type ResumeRejectReasonV2 = (typeof RESUME_REJECT_REASONS)[number];

export type ResumeResultV2 =
  | {
      v: 2;
      type: 'resumeResult';
      matchId: MatchId;
      playerId: ProtocolV2PlayerId;
      status: 'resumed';
      inputEpoch: number;
      eventCursor: number;
    }
  | {
      v: 2;
      type: 'resumeResult';
      matchId: MatchId;
      playerId: ProtocolV2PlayerId;
      status: 'full-resync';
      inputEpoch: number;
      eventCursor: number;
      reason: ResyncReasonV2;
    }
  | {
      v: 2;
      type: 'resumeResult';
      matchId: MatchId;
      playerId: ProtocolV2PlayerId;
      status: 'rejected';
      reason: ResumeRejectReasonV2;
    };

export const MATCH_CONNECTION_STATES = [
  'connecting',
  'negotiating',
  'waiting-seat',
  'connected',
  'reconnecting',
  'resuming',
  'resyncing',
  'failed',
] as const;
export type MatchConnectionStateName = (typeof MATCH_CONNECTION_STATES)[number];

export const MATCH_CONNECTION_FAILURE_REASONS_V2 = [
  'authentication-required',
  'connection-lost',
  'connection-timeout',
  'identity-mismatch',
  'malformed-message',
  'match-expired',
  'match-not-found',
  'missing-feature',
  'resume-expired',
  'runtime-mismatch',
  'seat-unavailable',
  'server-capacity',
  'transport-error',
  'unsupported-protocol',
] as const;
export type MatchConnectionFailureReasonV2 = (typeof MATCH_CONNECTION_FAILURE_REASONS_V2)[number];

/** User-visible state is data only. It never contains a transport Error,
 * credential, socket/room object, raw message or caller-controlled text. */
export type MatchConnectionState =
  | { state: 'connecting' | 'negotiating' | 'waiting-seat' | 'connected' | 'resuming' }
  | {
      state: 'reconnecting';
      attempt: number;
      /** Transport grace deadline in Unix milliseconds; never a sim clock. */
      deadlineMs: number;
    }
  | { state: 'resyncing'; reason: ResyncReasonV2 }
  | { state: 'failed'; reason: MatchConnectionFailureReasonV2; canRetry: boolean };

export interface MatchConnectionStateSourceV2 {
  connectionState(): MatchConnectionState;
  subscribeConnectionState(listener: (state: MatchConnectionState) => void): () => void;
}

export type ClientMessageV2<TCommand> = ClientHelloV2 | CommandEnvelopeV2<TCommand> | EventCursorAckV2 | ResumeRequestV2;
export type ServerMessageV2<TPayload> = SeatNegotiationOutcomeV2 | CommandResultV2 | SnapshotEnvelopeV2<TPayload> | ResumeResultV2 | MatchResultV2;

export interface ProtocolRequirement {
  requiredProtocol: SupportedProtocolVersion;
  requiredFeatures: ProtocolFeatureV2[];
}

export type ProtocolNegotiationResult =
  | { ok: true; selectedProtocol: SupportedProtocolVersion; features: ProtocolFeatureV2[] }
  | { ok: false; reason: 'unsupported-protocol' | 'unsupported-feature'; missingFeatures?: ProtocolFeatureV2[] };

/** Metadata required to recreate and verify a recorded match. */
export interface MatchReplayMetadataV1 {
  schemaVersion: 1;
  matchId: MatchId;
  protocolVersion: SupportedProtocolVersion;
  runtimeContractDigest: string;
  simDigest: string;
  capabilityDigest: string;
  configDigest: string;
  matchConfig: import('./match.js').MatchConfigV1;
}

/** Structural helper for preserving canonical envelope fields during projection. */
export function withProjectedPayloadV2<TCanonicalPayload, TViewerPayload>(
  envelope: SnapshotEnvelopeV2<TCanonicalPayload>,
  projectedPayload: TViewerPayload,
): SnapshotEnvelopeV2<TViewerPayload> {
  return { ...envelope, payload: projectedPayload };
}

/** Server-issued match-scoped public identity, never the backend auth UID.
 * A string type alone does not establish authenticated seat ownership. */
export type ProtocolV2PlayerId = string;
